// AlignUI VerticalBarChart v0.0.0
//
// Горизонтальные столбцы: «сравнить между собой» — какие объекты дороже, у кого долг больше.
// Величину кодирует длина, заливка плоская (правило графиков: градиента нет).
//
// Три правки против исходника набора, все — подстановка НАШИХ данных вместо демонстрационных:
//
//   1. Подпись столбца и деления оси. У AlignUI и то и другое считает `utils/number-formatter`
//      через `Intl.NumberFormat('en-US', { currency: 'USD' })`, то есть доллары. Формат
//      приходит снаружи: тем же столбцом показываются и суммы, и люди, и часы.
//   2. Высота. Была зашита в 86 — ровно под три столбца демонстрации. Восемь объектов при
//      такой высоте налезают друг на друга.
//   3. Ширина колонки подписей. Была 48 точек: «Housing» влезает, «ООО «БурСервис»» — нет.

import * as React from 'react';
import { type TooltipArrowProps } from '@radix-ui/react-tooltip';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
  type LabelProps,
} from 'recharts';

import { tooltipVariants } from '@/shared/ui/tooltip';
import { type ChartConfig } from '@/shared/ui/chart';

const YAxisLeftTick = ({ y, payload: { value } }: any) => (
  <Text
    x={0}
    y={y}
    textAnchor='start'
    verticalAnchor='middle'
    className='!fill-text-strong-950 text-paragraph-xs'
  >
    {value}
  </Text>
);

type CustomTooltipProps = React.ComponentProps<typeof RechartsTooltip> & {
  renderContent: (props: { payload: any }) => React.ReactNode;
};

const CustomTooltip = ({ active, payload, renderContent }: CustomTooltipProps) => {
  const { arrow, content } = tooltipVariants({
    size: 'small',
    variant: 'dark',
  });

  if (active && payload && payload.length) {
    return (
      <div className='-translate-x-1/2 -translate-y-full'>
        <div className={content()}>
          {renderContent({ payload })}
          <div className='absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full'>
            <div className={arrow()} />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const CustomSvgLabel = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  position,
  hoveredBar,
  id,
}: LabelProps & {
  hoveredBar: string;
  id?: string;
}) => {
  if (id !== hoveredBar) return null;

  const offsetX = position === 'insideRight' ? Number.parseFloat(width as string) - 12 : 0;
  const offsetY = Number.parseFloat(height as string) / 2;

  return (
    <svg
      x={Number.parseFloat(x as string) + offsetX}
      y={Number.parseFloat(y as string) + offsetY - 4}
      width={8}
      height={8}
      viewBox='0 0 8 8'
    >
      <defs>
        <filter id='dropShadow' x='-50%' y='-50%' width='200%' height='200%'>
          <feDropShadow
            dx='0'
            dy='1.5'
            stdDeviation='1.5'
            floodColor='#1B1C1D'
            floodOpacity='0.04'
          />
        </filter>
      </defs>
      <circle
        cx={4}
        cy={4}
        r={3.25}
        fill='transparent'
        className='stroke-illustration-white-0'
        strokeWidth={1.5}
        filter='url(#dropShadow)'
      />
    </svg>
  );
};

export type VerticalBarChartProps = {
  data: { id: string; name: string; value: number }[];
  chartConfig: ChartConfig;
  /** Как подписать величину под курсором. */
  formatValue: (value: number) => string;
  /**
   * Писать величину В КОНЦЕ каждого столбца.
   *
   * С ней ось не нужна вовсе, и это не экономия места. Ось отвечает на вопрос «примерно
   * сколько», и чтобы получить точное число, человеку приходится наводить курсор — то есть
   * знать, что там что-то есть, и уметь этим пользоваться. Подпись на столбце отвечает сразу
   * и всем, включая того, кто мышью не водит.
   */
  showValues?: boolean;
  /** Высота всей картинки. По 24 точки на столбец плюс место под ось — считает вызывающий. */
  height?: number;
  /** Ширина колонки подписей слева. */
  yAxisWidth?: number;
};

export default function VerticalBarChart({
  data,
  chartConfig,
  formatValue,
  showValues = false,
  height = 86,
  yAxisWidth = 48,
}: VerticalBarChartProps) {
  const [tooltipPos, setTooltipPos] = React.useState<TooltipArrowProps>({
    x: undefined,
    y: undefined,
  });
  const [hoveredBar, setHoveredBar] = React.useState('');

  // Место справа под подпись величины. Без него самый длинный столбец упирается в край, и
  // его собственная подпись уезжает за карточку.
  const полеСправа = showValues ? 104 : 28;

  const tooltipContent = ({ payload }: { payload: any }): React.ReactNode => {
    return <>{formatValue(Number(payload[0].value))}</>;
  };

  return (
    <ResponsiveContainer height={height} className='w-full'>
      <BarChart
        data={data}
        layout='vertical'
        margin={{ left: 14, right: полеСправа, top: 0, bottom: 0 }}
        barSize={16}
        onMouseMove={(e) => {
          setHoveredBar(e.activePayload?.[0].payload.id);
        }}
        onMouseLeave={() => setHoveredBar('')}
      >
        {/* Сетка размечает ОСЬ. Когда величина написана на столбце, оси нет, и пунктирные
            вертикали превращаются в линии неизвестно чего посреди карточки. */}
        {!showValues && (
          <CartesianGrid
            strokeDasharray='2 2'
            horizontal={false}
            className='stroke-illustration-soft-200'
          />
        )}
        {/* Ось скрыта, когда величина написана на самом столбце: два ответа на один вопрос
            рядом — это лишняя работа глазу, а точный ответ даёт подпись, а не деление. */}
        <XAxis
          hide
          type='number'
          dataKey='value'
          domain={[0, 'dataMax']}
          height={showValues ? 0 : 16}
        />
        <YAxis
          dataKey='name'
          type='category'
          axisLine={false}
          tickLine={false}
          interval={0}
          tick={<YAxisLeftTick />}
          width={yAxisWidth}
        />
        <RechartsTooltip
          content={<CustomTooltip renderContent={tooltipContent} />}
          cursor={false}
          isAnimationActive={true}
          offset={0}
          animationDuration={200}
          position={{
            x: tooltipPos.x as number,
            y: tooltipPos.y as number,
          }}
          allowEscapeViewBox={{
            x: true,
            y: true,
          }}
        />
        <Bar
          dataKey='value'
          radius={2}
          onMouseEnter={(e: any) => {
            setTooltipPos({ x: e.width + e.x - 8, y: e.y - 8 });
          }}
        >
          <LabelList
            position='insideRight'
            offset={4}
            content={(props) => <CustomSvgLabel hoveredBar={hoveredBar} {...props} />}
          />
          {showValues && (
            <LabelList
              dataKey='value'
              position='right'
              offset={8}
              className='fill-text-strong-950 text-label-xs'
              formatter={(значение: number) => formatValue(Number(значение))}
            />
          )}
          {data.map((d) => (
            <Cell key={d.id} fill={chartConfig[d.id]?.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
